import React, { useState } from 'react';
import { X, Camera, ChefHat, Settings, Wine, Zap, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GUIDE_SECTIONS } from '../content/guideContent';

const ICONS = {
    vision: Camera,
    creation: ChefHat,
    preferences: Settings,
    sommelier: Wine,
    power: Zap
};

const GuideModal = ({ isOpen, onClose }) => {
    const [activeTabId, setActiveTabId] = useState('vision');

    if (!isOpen) return null;

    const activeSection = GUIDE_SECTIONS.find(s => s.id === activeTabId) || GUIDE_SECTIONS[0];

    return (
        <div className="fixed inset-0 z-[160] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />

            <div className="relative w-full max-w-4xl h-[80vh] bg-[var(--glass-surface)] border border-[var(--glass-border)] rounded-[2.5rem] shadow-2xl overflow-hidden animate-pop-in flex flex-col md:flex-row">

                {/* MOBILE HEADER (Visible only on small screens) */}
                <div className="md:hidden flex items-center justify-between p-6 border-b border-[var(--glass-border)] bg-[var(--glass-surface)]">
                    <h2 className="text-xl font-black text-[var(--text-primary)] flex items-center gap-2">
                        <BookOpen className="w-6 h-6 text-[var(--accent-color)]" />
                        Kitchen Guide
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-[var(--text-secondary)]/10 rounded-full transition-colors">
                        <X className="w-6 h-6 text-[var(--text-primary)]" />
                    </button>
                </div>

                {/* SIDEBAR NAVIGATION */}
                <div className="w-full md:w-72 bg-[var(--glass-surface)] md:border-r border-[var(--glass-border)] flex-none flex flex-row md:flex-col overflow-x-auto md:overflow-y-auto no-scrollbar">

                    <div className="hidden md:flex p-8 pb-4 items-center justify-between">
                        <h2 className="text-2xl font-black text-[var(--text-primary)] flex items-center gap-2">
                            <BookOpen className="w-7 h-7 text-[var(--accent-color)]" />
                            Guide
                        </h2>
                    </div>

                    <div className="flex md:flex-col gap-2 p-4 md:p-6 w-full">
                        {GUIDE_SECTIONS.map((section) => {
                            const Icon = ICONS[section.id];
                            const isActive = activeTabId === section.id;

                            return (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveTabId(section.id)}
                                    className={`flex-none flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-auto md:w-full whitespace-nowrap md:whitespace-normal
                    ${isActive
                                            ? 'bg-[var(--accent-color)] text-white shadow-lg shadow-blue-500/30 font-bold'
                                            : 'text-[var(--text-secondary)] hover:bg-[var(--text-secondary)]/10 font-medium'
                                        }`}
                                >
                                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : ''}`} />
                                    <span>{section.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="hidden md:block mt-auto p-6">
                        <button
                            onClick={onClose}
                            className="w-full py-3 bg-[var(--text-secondary)]/10 hover:bg-[var(--text-secondary)]/20 rounded-xl text-[var(--text-secondary)] font-bold transition-all"
                        >
                            Close Guide
                        </button>
                    </div>
                </div>

                {/* CONTENT AREA */}
                <div className="flex-1 flex flex-col h-full overflow-hidden bg-[var(--card-surface)]/50">
                    {/* DESKTOP HEADER (Close button only) */}
                    <div className="hidden md:flex justify-end p-6 pb-2">
                        <button onClick={onClose} className="p-2 hover:bg-[var(--text-secondary)]/10 rounded-full transition-colors">
                            <X className="w-6 h-6 text-[var(--text-secondary)]" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 md:p-10 custom-scrollbar">
                        <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500 key={activeTabId}"> {/* Re-animate on tab change */}
                            <div className="mb-6">
                                <span className="text-xs font-black text-[var(--accent-color)] uppercase tracking-widest bg-[var(--accent-color)]/10 px-3 py-1 rounded-lg">
                                    {activeSection.label}
                                </span>
                                <h1 className="text-3xl md:text-4xl font-black text-[var(--text-primary)] mt-4 mb-2">
                                    {activeSection.title}
                                </h1>
                                <div className="h-1 w-20 bg-[var(--accent-color)] rounded-full" />
                            </div>

                            <div className="markdown-content prose prose-invert prose-p:text-[var(--text-secondary)] prose-headings:text-[var(--text-primary)] prose-strong:text-[var(--text-primary)] prose-strong:font-black prose-a:text-[var(--accent-color)]">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {activeSection.content}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default GuideModal;
